import { useState } from "react";
import { notification } from "antd";

export default function useEmissionPointWizard({ mode, form }) {
  const isCreate = mode === "create";
  const [activeTab, setActiveTab] = useState("1");

  const hasAtLeastOnePositiveRate = (rates = []) =>
    rates.some(
      (r) => typeof r?.emissionRate === "number" && r.emissionRate > 0
    );

  const getMinPpmMap = (minList = []) =>
    new Map(minList.map((x) => [x.elementId, x.minPpm]));

  const next = async () => {
    await handleTabChange(String(Number(activeTab) + 1));
  };

  const back = () => {
    setActiveTab(String(Number(activeTab) - 1));
  };

  const handleTabChange = async (nextKey) => {
    if (!isCreate) {
      setActiveTab(nextKey);
      return;
    }

    if (activeTab === "1" && nextKey === "2") {
      try {
        await form.validateFields([
          "name",
          "latitude",
          "longitude",
          "height",
          "anemometerDeviceId",
          "concentrationHeight",
          "windThreshold",
        ]);
        setActiveTab("2");
      } catch {
        return;
      }
    }

    if (activeTab === "2" && nextKey === "3") {
      const rates = form.getFieldValue("emissionRates") || [];

      if (!hasAtLeastOnePositiveRate(rates)) {
        warn({
          message: "Emission point is incomplete",
          description:
            "At least one emission rate greater than 0 must be defined.",
          placement: "top",
        });
        return;
      }

      setActiveTab("3");
      return;
    }

    if (activeTab === "3" && nextKey === "4") {
      const rates = form.getFieldValue("emissionRates") || [];
      const minList = form.getFieldValue("minPpmByElement") || [];
      const minMap = getMinPpmMap(minList);

      const missingMin = rates
        .filter((r) => r?.emissionRate > 0)
        .filter(
          (r) =>
            !(
              typeof minMap.get(r.elementId) === "number" &&
              minMap.get(r.elementId) > 0
            )
        );

      if (missingMin.length > 0) {
        warn({
          message: "Missing Min PPM values",
          description:
            "Each defined emission rate must have a corresponding Min PPM value greater than 0.",
          placement: "top",
        });
        return;
      }

      setActiveTab("4");
      return;
    }

    if (Number(nextKey) < Number(activeTab)) {
      setActiveTab(nextKey);
    }
  };

  const warn = (config) => {
    setTimeout(() => {
      notification.warning(config);
    }, 0);
  };

  // 👇 SOLO PARA CREATE
  const validateBeforeSave = async () => {
    if (!isCreate) return;

    await form.validateFields();

    const rates = form.getFieldValue("emissionRates") || [];
    if (!hasAtLeastOnePositiveRate(rates)) {
      setActiveTab("2");
      warn({
        message: "Emission point is incomplete",
        description:
          "At least one emission rate greater than 0 must be defined.",
        placement: "top",
      });
      throw new Error("Emission rates invalid");
    }

    const minList = form.getFieldValue("minPpmByElement") || [];
    const minMap = getMinPpmMap(minList);

    const missingMin = validateRatesHaveMinPpm();
    if (missingMin.length > 0) {
      setActiveTab("3");
      warn({
        message: "Missing Min PPM values",
        description:
          "Each defined emission rate must have a corresponding Min PPM value greater than 0.",
        placement: "top",
      });
      throw new Error("Min PPM invalid");
    }
  };

  const validateRatesHaveMinPpm = () => {
    const rates = form.getFieldValue("emissionRates") || [];
    const minList = form.getFieldValue("minPpmByElement") || [];

    const elementsWithMin = new Set(
      minList
        .filter(m => typeof m?.minPpm === "number" && m.minPpm > 0)
        .map(m => String(m.elementId))
    );

    return rates
      .filter(r => typeof r?.emissionRate === "number" && r.emissionRate > 0)
      .filter(r => !elementsWithMin.has(String(r.elementId)));
  };

  return {
    activeTab,
    setActiveTab,
    handleTabChange,
    next,
    back,
    validateBeforeSave,
    validateRatesHaveMinPpm,

    isCreate,
    isFirst: activeTab === "1",
    isLast: activeTab === "4",
  };
}
